import { useMemo, useState } from 'react';

import { Box, Typography } from '@mui/material';

import ExportChartButton from '@/components/Chart/ExportButton/ExportChartButton';
import Loader from '@/components/Loader';
import TabPanel from '@/components/TabPanel';
import Tabs from '@/components/Tabs';
import useUserDetails from '@/hooks/useUserDetails';
import { FactsheetDetails } from '@/pages/Facthsheets/constants';
import {
  AuthenticatedFwdCurveChart,
  AuthenticatedSpotChart
} from '@/pages/Facthsheets/FactsheetView/FactsheetWindow/FactsheetBody/Tabs/ChartTab/AuthenticatedChart';
import UnauthenticatedChart from '@/pages/Facthsheets/FactsheetView/FactsheetWindow/FactsheetBody/Tabs/ChartTab/UnauthenticatedChart';
import { TemplateChartSeries } from '@/pages/Markets/MarketsComponents/types';

export const BasicUserMessage = () => {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
      <Typography sx={{ fontWeight: 600, fontSize: '24px' }}>This is a Premium feature.</Typography>
      <Typography sx={{ fontWeight: 600, fontSize: '24px' }}>
        Upgrade to GX Go Premium to enable charts and unlock advanced functionality.
      </Typography>
    </Box>
  );
};

const BlurBackdrop = () => {
  const { isUserPremium, isUserExpired } = useUserDetails();
  if (isUserPremium && !isUserExpired) {
    return null;
  }
  return (
    <>
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backdropFilter: 'blur(4px)',
          zIndex: 1400
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: '20%',
          left: '50%',
          textAlign: 'center',
          transform: 'translateX(-50%)',
          zIndex: 1401,
          width: '100%'
        }}
      >
        {(!isUserPremium || isUserExpired) && <BasicUserMessage />}
      </Box>
    </>
  );
};
const ChartTab = ({ factsheet }: { factsheet: FactsheetDetails }) => {
  const spotAlias = factsheet.indexDetails['spotAlias'] as string;
  const { isUserPremium, isUserExpired, status } = useUserDetails();
  const [activeTab, setActiveTab] = useState(0);
  const isAuthorized = status !== undefined && isUserPremium && !isUserExpired;
  const templateSpotSeries = useMemo(() => {
    if (factsheet.spotSymbols.length > 0) {
      const symbol = factsheet.spotSymbols[0];
      if (isUserPremium && !isUserExpired && symbol) {
        const factsheetSeries: TemplateChartSeries = {
          settings: {
            type: 'line'
          },
          shortTitle:
            spotAlias ??
            `${factsheet.code} ${symbol.timeRef} ${symbol.periodType} ${symbol.period}`,
          symbol: {
            group: 'Prod_Indexes',
            key: {
              Code: factsheet.code,
              PeriodType: symbol.periodType,
              TimeRef: symbol.timeRef,
              Period: symbol.period
            }
          }
        };

        return [factsheetSeries];
      }
    }
    return [];
  }, [factsheet, isUserExpired, isUserPremium, spotAlias]);

  const templateForwardSeries = useMemo(() => {
    if (factsheet.fwdCurveSymbols.length > 0) {
      const symbol = factsheet.fwdCurveSymbols[0];
      if (isUserPremium && !isUserExpired && symbol) {
        const factsheetSeries: TemplateChartSeries = {
          settings: {
            type: 'line'
          },
          shortTitle:
            spotAlias ??
            `${factsheet.code} ${symbol.timeRef} ${symbol.periodType} ${symbol.period}`,
          symbol: {
            group: 'Prod_Indexes',
            key: {
              Code: factsheet.code,
              PeriodType: symbol.periodType,
              TimeRef: symbol.timeRef
            }
          }
        };
        return [factsheetSeries];
      }
    }
    return [];
  }, [factsheet, isUserExpired, isUserPremium, spotAlias]);
  const contentTabs = [];
  if (factsheet.spotSymbols.length > 0) {
    contentTabs.push({
      position: 0,
      name: 'Spot',
      component: <AuthenticatedSpotChart templateChartSeries={templateSpotSeries} />
    });
  }
  if (factsheet.fwdCurveSymbols.length > 0) {
    contentTabs.push({
      position: 1,
      name: 'Forward',
      component: <AuthenticatedFwdCurveChart templateChartSeries={templateForwardSeries} />
    });
  }
  if ('loading' === status) {
    return <Loader />;
  }
  return (
    <>
      {isAuthorized && contentTabs.length > 1 && (
        <Box sx={{ display: 'flex', flex: 1, justifyContent: 'space-between' }}>
          <Tabs
            activeTab={activeTab}
            tabs={contentTabs.map(({ name }) => ({ name }))}
            onChange={setActiveTab}
          />

          <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
            <ExportChartButton tabName={contentTabs[activeTab]?.name ?? ''} />
          </Box>
        </Box>
      )}

      <Box
        sx={{
          position: 'relative'
        }}
      >
        <BlurBackdrop />

        {status === undefined || ((!isUserPremium || isUserExpired) && <UnauthenticatedChart />)}
        {templateSpotSeries.length === 0 &&
          templateForwardSeries.length === 0 &&
          isUserPremium &&
          !isUserExpired && (
            <Box
              sx={{
                width: '100%',
                height: 300
              }}
            >
              <Typography>{factsheet.code} does not have any spot items</Typography>
            </Box>
          )}
        {isAuthorized &&
          contentTabs.map((tab, index) => (
            <TabPanel
              key={factsheet.code + index}
              containerSX={{
                flex: 1,
                flexGrow: 1,
                width: '100%',
                overflow: 'hidden',
                ...(index === activeTab ? { display: 'flex' } : {})
              }}
              index={index}
              sx={{
                pt: 2,
                flex: 1,
                flexGrow: 1,
                width: '100%',
                overflow: 'auto'
              }}
              value={activeTab}
            >
              {tab.component}
            </TabPanel>
          ))}
      </Box>
    </>
  );
};
export default ChartTab;
